
import React, { useState, useEffect } from 'react';
import EditBar from './EditBar';
import EditEvent from './EditEvent';

import useServerApi from '../../../../hooks/useServerApi';
import AudioPlayer from '../AudioPlayer';
import SimplePagination from './SimplePagination';

import {
    Box,
    Flex,

    Badge,
    IconButton,
    Text,
    Card,
    CardBody,
    CardHeader,
    Heading,
    Stack,
    Tooltip,
    Collapse,
    useToast,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Textarea,
    useDisclosure,
    Link,
} from '@chakra-ui/react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ShareCallDetails from './ShareCallDetails';
import {
    faChevronDown,
    faChevronUp,
    faTrash,
    faPhoneSlash,
    faPhone,
    faTrashRestore,
    faPaperPlane,
    faStickyNote,
    faComments
} from '@fortawesome/free-solid-svg-icons';
import { formatUSPhoneNumber } from '../../../../utils/phone.js'


import { IncomingIcon } from '../../../icons/IncomingIcon';
import TransferredIcon from '../../../../icons/TransferredIcon';


// Constants
const MAX_NOTE_PREVIEW_LENGTH = 150;

// Trash/Untrash button component


const DataDisplay = ({ calls, loading, filters, refreshData, onPageChange, currentPage, totalPages, editMode, setEditMode }) => {
    // State to track which card is expanded
    const [expandedCallId, setExpandedCallId] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [localEvents, setLocalEvents] = useState({});
    const toast = useToast();

    // State for notes and feedback modals
    const [activeCallId, setActiveCallId] = useState(null);
    const [notesText, setNotesText] = useState('');
    const [feedbackText, setFeedbackText] = useState('');
    const [savingNotes, setSavingNotes] = useState(false);
    const [savingFeedback, setSavingFeedback] = useState(false);

    const {
        isOpen: isNotesOpen,
        onOpen: onNotesOpen,
        onClose: onNotesClose
    } = useDisclosure();

    const {
        isOpen: isFeedbackOpen,
        onOpen: onFeedbackOpen,
        onClose: onFeedbackClose
    } = useDisclosure();

    // State for edit event modal
    const [editEventData, setEditEventData] = useState(null);
    const {
        isOpen: isEditEventOpen,
        onOpen: onEditEventOpen,
        onClose: onEditEventClose
    } = useDisclosure();

    // Select all handler (store call objects)
    const isAllSelected = selectedIds.length === calls.length && calls.length > 0;
    const handleSelectAll = (checked) => {
        setSelectedIds(checked ? [...calls] : []);
    };

    const handleSelectOne = (call, checked) => {
        setSelectedIds(prev => checked ? [...prev, call] : prev.filter(x => (x.id || x.call_sid) !== (call.id || call.call_sid)));
    };

    // Function to toggle the expanded state of a call
    const toggleExpand = (callId) => {
        setExpandedCallId(expandedCallId === callId ? null : callId);
    }

    const [{ post, get, put }] = useServerApi();

    useEffect(() => {
        const events = {};
        calls.forEach(call => {
            if (call.event_booked) {
                events[call.call_sid] = call.event_booked;
            }
        });
        setLocalEvents(events);
    }, [calls]);

    // Refetch event metadata for a given callId
    const refetchEvent = async (callId) => {
        if (callId) {
            const call = calls.find(c => c.call_sid === callId);
            if (call && call.event_booked) {
                const bookedId = call.event_booked.id;
                const [success, result, error] = await get(`/rest_endpoints/call_history/booked_event/${bookedId}`);
                if (success) {
                    let existingEvent = call.event_booked;
                    existingEvent.metadata = result.metadata; // Update metadata in local state
                    setLocalEvents(prev => ({ ...prev, [callId]: existingEvent }));
                } else {
                    console.error(error);
                }
            }
        }
    };

    useEffect(() => {
        refetchEvent(expandedCallId);
    }, [expandedCallId, calls, get]);

    const handleOpenNotes = (call) => {
        setActiveCallId(call.id);
        onNotesOpen();
    };

    const handleOpenFeedback = (call) => {
        setActiveCallId(call.id);
        onFeedbackOpen();
    };

    const handleSaveNotes = async () => {
        setSavingNotes(true);
        try {
            const [success, result] = await post('/rest_endpoints/call_history/save_notes', {
                call_activity_id: activeCallId,
                notes: notesText
            });

            if (success && result?.message) {
                toast({ status: 'success', title: result.message });

                setNotesText('');
                onNotesClose();
                refreshData();
            } else if (result?.detail) {
                toast({ status: 'error', title: result.detail });
            }
        } catch (err) {
            toast({ status: 'error', title: 'Error saving notes' });
        } finally {
            setSavingNotes(false);
        }
    };

    const handleSaveFeedback = async () => {
        setSavingFeedback(true);
        try {
            const [success, result] = await post('/rest_endpoints/call_history/save_feedback', {
                call_activity_id: activeCallId,
                feedback: feedbackText
            });

            if (success && result?.message) {
                toast({ status: 'success', title: result.message });
                setFeedbackText('');
                onFeedbackClose();
                refreshData();
            } else if (result?.detail) {
                toast({ status: 'error', title: result.detail });
            }
        } catch (err) {
            toast({ status: 'error', title: 'Error saving feedback' });
        } finally {
            setSavingFeedback(false);
        }
    };

    // Helper function to get last 2 non-empty lines from notes
    const getLastTwoNonEmptyLines = (notes) => {
        if (!notes) return [];
        return notes.split('\n')
            .filter(line => line.trim())
            .slice(-2);
    };

    // Helper function to truncate note text
    const truncateNote = (note) => {
        if (note.length > MAX_NOTE_PREVIEW_LENGTH) {
            return `${note.substring(0, MAX_NOTE_PREVIEW_LENGTH)}...`;
        }
        return note;
    };

    // Function to format the date string from the API to a relative format
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHr = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHr / 24);
        const diffWeek = Math.floor(diffDay / 7);

        if (diffMin < 5) return 'few mins ago';
        if (diffHr < 1) return `${diffMin} mins ago`;
        if (diffHr < 24) return `${diffHr} hrs ago`;
        if (diffDay === 1) return 'Yesterday';
        if (diffDay < 7) return `${diffDay} days back`;
        if (diffWeek === 1) return '1 week back';
        if (diffWeek > 1) return 'over a week back';
        return new Intl.DateTimeFormat('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        }).format(date);
    };

    // Function to get the full formatted date for tooltip
    const getFullDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        }).format(date);
    };

    // Function to format duration in seconds to mm:ss or hh:mm:ss
    const formatDuration = (seconds) => {
        if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0) return '';
        const sec = Math.floor(seconds % 60);
        const min = Math.floor((seconds / 60) % 60);
        const hr = Math.floor(seconds / 3600);
        const pad = (n) => n.toString().padStart(2, '0');
        if (hr > 0) {
            return `${pad(hr)}:${pad(min)}:${pad(sec)}`;
        }
        return `${pad(min)}:${pad(sec)}`;
    };

    // Helper to format event time range in local timezone
    const formatEventTimeRange = (start, end) => {
        if (!start || !end) return '';
        const startDate = new Date(start);
        const endDate = new Date(end);
        const options = {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        };
        return `${startDate.toLocaleTimeString([], options)} - ${endDate.toLocaleTimeString([], options)}`;
    };

    // Helper for underlined date
    const DateDisplay = ({ dateString }) => {
        const rel = formatDate(dateString);
        const full = getFullDate(dateString);
        const isOverWeek = rel === 'over a week back';
        return (
            <Tooltip label={rel} hasArrow openDelay={200} placement="top">
                <Text
                    fontSize="xs"
                    color="gray.500"
                    textDecoration={isOverWeek ? 'underline' : undefined}
                    mt={1}
                >
                    {full}
                </Text>
            </Tooltip>
        );
    };

    // Contact display helper
    const ContactDisplay = ({ contactInfo, number }) => {
        const hasContact = contactInfo && contactInfo.name;
        // Always show number with phone icon, and show name/email if available
        return (
            <Box>
                {hasContact && (
                    <Text
                        fontWeight="bold"
                        fontSize="md"
                        color="gray.800"
                        lineHeight={1}
                        noOfLines={1}
                        mb={1}
                    >
                        {contactInfo.name}
                    </Text>
                )}
                <Box display="flex" alignItems="center" gap={3}>
                    <Text
                        fontWeight={hasContact ? 'normal' : 'bold'}
                        fontSize={hasContact ? 'xs' : 'md'}
                        color="gray.800"
                        lineHeight={1}
                        noOfLines={1}
                        pt={hasContact ? 2 : 0}
                    >
                        {formatUSPhoneNumber(number || "Unknown")}
                    </Text>
                </Box>
                {contactInfo && contactInfo.email && (
                    <Text fontSize="xs" color="gray.600" mt={1}>
                        <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
                    </Text>
                )}
            </Box>
        );
    };

    // Calendar icon component
    const CalendarIcon = ({ size = 16 }) => (
        <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="5" width="14" height="12" rx="2" fill="currentColor" fillOpacity="0.1" />
            <rect x="3" y="5" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <rect x="7" y="2" width="1.5" height="4" rx="0.75" fill="currentColor" />
            <rect x="11.5" y="2" width="1.5" height="4" rx="0.75" fill="currentColor" />
        </svg>
    );

    const DurationDisplay = ({ duration, recording_uri, callSid }) => (
        <Flex
            alignItems={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={2}
            onClick={e => {
                e.stopPropagation();
            }}
        >
            {recording_uri && <AudioPlayer audioSrc={`${process.env.REACT_APP_SERVER_API_URL}/rest_endpoints/call_recording_p/${callSid}`} />}
            <Text fontWeight="medium" fontSize="md" w={{ base: "100%", md: "auto" }}>{formatDuration(duration)}</Text>
        </Flex>
    );

    const onTrashCalls = async (calls, isTrash, onComplete) => {
        if (!calls || calls.length === 0) return;
        // Separate calls to trash and untrash
        const toTrash = calls.filter(call => !call.is_delete);
        const toUntrash = calls.filter(call => call.is_delete);
        let error = false;
        // Trash calls
        if (isTrash && toTrash.length > 0) {
            try {
                const ids = toTrash.map(call => call.id);
                const [success, result] = await post('/rest_endpoints/call_history/trash_calls', { call_activity_ids: ids });
                if (success && result?.message) {
                    toast({ status: 'success', title: result.message });
                } else if (result?.detail) {
                    toast({ status: 'error', title: result.detail });
                    error = true;
                }
            } catch (err) {
                toast({ status: 'error', title: 'Error updating trash status' });
                error = true;
            }

        }
        // Untrash calls
        if (!isTrash && toUntrash.length > 0) {
            try {
                const ids = toUntrash.map(call => call.id || call.call_sid);
                const [success, result] = await post('/rest_endpoints/call_history/untrash_calls', { call_activity_ids: ids });
                if (success && result?.message) {
                    toast({ status: 'success', title: result.message });
                } else if (result?.detail) {
                    toast({ status: 'error', title: result.detail });
                    error = true;
                }
            } catch (err) {
                toast({ status: 'error', title: 'Error updating trash status' });
                error = true;
            }
        }
        if (onComplete && !error) onComplete();
    };


    const TrashUntrashButton = ({ call, onComplete }) => {
        const [loading, setLoading] = useState(false);

        // API handler for trash/untrash
        const handleTrashUntrash = async (e) => {
            e.stopPropagation();

            setLoading(true);
            await onTrashCalls([call], !call.is_delete, onComplete);
            setLoading(false);
        };

        return (
            <IconButton
                aria-label={call.is_delete ? 'Restore from trash' : 'Move to trash'}
                icon={call.is_delete ? <FontAwesomeIcon icon={faTrashRestore} /> : <FontAwesomeIcon icon={faTrash} />}
                size="sm"
                variant="ghost"
                colorScheme={call.is_delete ? 'green' : 'black'}
                ml={1}
                isLoading={loading}
                onClick={handleTrashUntrash}
                title={call.is_delete ? 'Restore from trash' : 'Move to trash'}
            />
        );
    };

    const onBlockNumbers = async (calls, block, onComplete) => {
        if (!calls || calls.length === 0) return;
        // Separate numbers to block and unblock
        const toBlock = calls.filter(call => !call.is_blocked);
        const toUnblock = calls.filter(call => call.is_blocked);
        let error = false;
        // Block numbers
        if (block && toBlock.length > 0) {
            try {
                const phone_numbers = toBlock.map(call => call.caller);
                const [success, result] = await post('/rest_endpoints/call_history/block_number', { phone_numbers });
                if (success && result?.message) {
                    toast({ status: 'success', title: result.message });
                } else if (result?.detail) {
                    toast({ status: 'error', title: result.detail });
                    error = true;
                }
            } catch (err) {
                toast({ status: 'error', title: 'Error updating block status' });
                error = true;
            }
        }
        // Unblock numbers
        if (!block && toUnblock.length > 0) {
            try {
                const phone_numbers = toUnblock.map(call => call.caller);
                const [success, result] = await post('/rest_endpoints/call_history/unblock_number', { phone_numbers });
                if (success && result?.message) {
                    toast({ status: 'success', title: result.message });
                } else if (result?.detail) {
                    toast({ status: 'error', title: result.detail });
                    error = true;
                }
            } catch (err) {
                toast({ status: 'error', title: 'Error updating block status' });
                error = true;
            }
        }
        if (onComplete && !error) onComplete();
    };

    const BlockNumberButton = ({ call, onComplete }) => {
        const [loading, setLoading] = useState(false);

        const onBlockNumber = async (e) => {
            e.stopPropagation();
            setLoading(true);
            try {
                onBlockNumbers([call], !call.is_blocked, refreshData);
            } catch (err) {
                toast({ status: 'error', title: 'Error updating block status' });
            } finally {
                setLoading(false);
            }
        };
        if (call.caller === "Web Call") {
            return null;
        }

        return (
            <IconButton
                aria-label={call.is_blocked ? 'Unblock number' : 'Block future calls'}
                icon={call.is_blocked ? <FontAwesomeIcon icon={faPhone} /> : <FontAwesomeIcon icon={faPhoneSlash} />}
                size="sm"
                colorScheme={call.is_blocked ? 'green' : 'red'}
                isLoading={loading}
                onClick={onBlockNumber}
                title={call.is_blocked ? 'Unblock number' : 'Block future calls'}
                ml={1}
            />
        );
    };
    const onBulkActionComplete = () => {
        setSelectedIds([]);
        setEditMode(false);
        refreshData();
    }
    const showEditIcons = [];
    if (!filters.blockedOnly) showEditIcons.push('block');
    if (filters.blockedOnly) showEditIcons.push('unblock');
    if (filters.isDelete) showEditIcons.push('untrash');
    if (!filters.isDelete) showEditIcons.push('trash');

    const handleEditEvent = async (bookedId) => {
        try {
            const [success, result, error] = await get(`/rest_endpoints/call_history/booked_event/${bookedId}`);
            if (success) {
                setEditEventData(result);
                onEditEventOpen();
            } else {
                console.error('Error fetching booked event', error);
            }
        } catch (err) {
            console.error('Error fetching booked event', err);
        }
    };

    const handleSaveEvent = async (updatedEvent) => {
        try {
            const [success, result, error] = await post(`/rest_endpoints/call_history/booked_event/${updatedEvent.id}/update`, {
                subject: updatedEvent.subject,
                description: updatedEvent.description
            });
            if (success) {
                toast({
                    title: "Event updated",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                refreshData();
            } else {
                console.error('Error updating event', error);
                toast({
                    title: "Error updating event",
                    description: error.message || "Something went wrong",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (err) {
            console.error('Error updating event', err);
            toast({
                title: "Error updating event",
                description: err.message || "Something went wrong",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };


    return (
        <Box maxWidth="full" p="12px">
            {editMode && (
                <EditBar
                    allSelected={isAllSelected}
                    onSelectAll={handleSelectAll}
                    totalCount={calls.length}
                    selectedCount={selectedIds.length}
                    onBlockClick={(block) => {
                        onBlockNumbers([...selectedIds], block, onBulkActionComplete);
                    }}
                    onTrashClick={(trash) => {
                        onTrashCalls([...selectedIds], trash, onBulkActionComplete);
                    }}
                    showIcons={showEditIcons}
                    onDoneClick={() => { setSelectedIds([]); setEditMode(false); }}
                />
            )}
            <Stack spacing={4}>
                {calls.map((call) => {
                    // Check for transfer number
                    const transferNumber = call?.extracted_call_details?.meta_data?.transfer_number;
                    const showTransferIcon = transferNumber && transferNumber.trim() !== "";

                    // Check for notified_emails
                    const notifiedEmails = call?.extracted_call_details?.meta_data?.notified_emails;
                    const showEmailForwardedIcon = Array.isArray(notifiedEmails) && notifiedEmails.length > 0;

                    // Check for event booking
                    const eventBooked = localEvents[call.call_sid];
                    const showEventBadge = !!eventBooked;
                    return (
                        <Card
                            key={call.id || call.call_sid}
                            boxShadow={expandedCallId === call.call_sid ? "md" : "sm"}
                            borderColor={expandedCallId === call.call_sid ? "blue.300" : "gray.200"}
                            borderWidth={expandedCallId === call.call_sid ? 2 : 1}
                            borderRadius="lg"
                            bg={expandedCallId === call.call_sid ? "blue.50" : "white"}
                            cursor="pointer"
                            transition="box-shadow 0.2s"
                            onClick={() => toggleExpand(call.call_sid)}
                        >
                            <CardHeader p={4} display="flex" alignItems="center" justifyContent="space-between">
                                <Flex w="100%" alignItems="center" justifyContent="space-between" flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                                    {/* Left: Incoming call icon and number */}
                                    <Flex alignItems="center" gap={3} flex="0 0 auto" w={{ base: '100%', md: '30%' }} minWidth={0}>
                                        {editMode && (
                                            <input
                                                type="checkbox"
                                                style={{ marginRight: 8 }}
                                                checked={selectedIds.some(x => (x.id) === (call.id))}
                                                onChange={e => {
                                                    e.stopPropagation();
                                                    handleSelectOne(call, e.target.checked)
                                                }}
                                                onClick={e => e.stopPropagation()}
                                            />
                                        )}
                                        <Box bg="green.100" p={2} borderRadius="full" display="flex" alignItems="center" justifyContent="center">
                                            <IncomingIcon boxSize={7} color="green.500" />
                                        </Box>
                                        <Box>
                                            <ContactDisplay contactInfo={call.contact_info} number={call.caller} />
                                            <DateDisplay dateString={call.start_time} />
                                        </Box>
                                    </Flex>
                                    {/* Center: Duration */}
                                    <Flex justifyContent="left" w={{ base: '100%', md: '30%' }} minWidth={0}>
                                        <Box w="100%" maxWidth={{ base: '100%', md: '320px' }}>
                                            <DurationDisplay duration={call.call_duration} recording_uri={call.recording_uri} callSid={call.call_sid || call.id} />
                                        </Box>
                                    </Flex>
                                    {/* Right: Summary and expand */}
                                    <Flex alignItems="center" gap={2} flex="0 0 auto" flexWrap={{ base: 'wrap', md: 'nowrap' }} w={{ base: '100%', md: '40%' }} minWidth={0} justifyContent={{ base: 'flex-start', md: 'flex-end' }}>
                                        {showTransferIcon && (
                                            <Badge
                                                colorScheme="purple"
                                                fontSize="sm"
                                                px={2}
                                                py={1}
                                                borderRadius="md"
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                title={`Transferred to ${transferNumber}`}
                                            >
                                                <TransferredIcon size={16} />
                                                <span style={{ marginLeft: 4 }}>Transferred</span>
                                            </Badge>
                                        )}
                                        {showEventBadge && (
                                            <Badge
                                                colorScheme="teal"
                                                fontSize="sm"
                                                px={2}
                                                py={1}
                                                borderRadius="md"
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                            >
                                                <CalendarIcon />
                                                <Text>
                                                    {formatEventTimeRange(eventBooked.event_start_time, eventBooked.event_end_time)}
                                                </Text>
                                            </Badge>
                                        )}
                                        <IconButton
                                            aria-label="Toggle expansion"
                                            icon={<FontAwesomeIcon icon={expandedCallId === call.call_sid ? faChevronUp : faChevronDown} />}
                                            size="sm"
                                            variant="ghost"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleExpand(call.call_sid);
                                            }}
                                        />
                                        <BlockNumberButton call={call} onComplete={refreshData} />
                                        <ShareCallDetails call={call} />
                                        <TrashUntrashButton call={call} onComplete={refreshData} />
                                    </Flex>
                                </Flex>
                            </CardHeader>
                            <Collapse in={expandedCallId === call.call_sid} animateOpacity>
                                <CardBody p={6} pt={0}>
                                    {/* Feedback and Notes Buttons */}
                                    <Flex gap={3} mb={4}>
                                        <Button
                                            leftIcon={<FontAwesomeIcon icon={faComments} />}
                                            colorScheme="blue"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenFeedback(call);
                                            }}
                                        >
                                            Feedback
                                        </Button>
                                        <Button
                                            leftIcon={<FontAwesomeIcon icon={faStickyNote} />}
                                            colorScheme="teal"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenNotes(call);
                                            }}
                                        >
                                            Notes
                                        </Button>
                                    </Flex>
                                    <Stack direction={{ base: 'column', md: 'row' }} spacing={10}>
                                        {/* Summary Section */}
                                        <Box flex={1}>
                                            <Heading size="md" mb={4}>Summary</Heading>
                                            <Text>{call.summary || "No summary available."}</Text>
                                            {showTransferIcon && (
                                                <Box mt={3} p={2} bg="purple.50" borderRadius="md" display="flex" alignItems="center" gap={2}>
                                                    <TransferredIcon size={16} />
                                                    <Text fontSize="sm" color="purple.700">Transferred to <b>{transferNumber}</b></Text>
                                                </Box>
                                            )}
                                            {/* Booked Event Section */}
                                            {eventBooked && (
                                                <Box mt={4} p={4} bg="teal.50" borderRadius="md">
                                                    <Heading size="sm" mb={3} display="flex" alignItems="center" gap={2}>
                                                        <CalendarIcon />
                                                        <Text>Booked Event</Text>
                                                        <Box ml="auto" display="flex" gap={2} alignItems="center">
                                                            {eventBooked.metadata?.htmlLink && (
                                                                <Link href={eventBooked.metadata.htmlLink} isExternal color="blue.500" fontSize="sm">
                                                                    View in Google
                                                                </Link>
                                                            )}
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                onClick={(e) => { e.stopPropagation(); handleEditEvent(eventBooked.id); }}
                                                            >
                                                                Edit
                                                            </Button>
                                                        </Box>
                                                    </Heading>
                                                    <Stack spacing={2}>
                                                        <Text fontWeight="bold">{eventBooked.name}</Text>
                                                        <Text fontSize="sm">Subject: {eventBooked.subject}</Text>
                                                        <Text fontSize="sm" whiteSpace="pre-line">{eventBooked.description}</Text>
                                                        <Text fontSize="sm">
                                                            <Text as="span" fontWeight="semibold">Time: </Text>
                                                            {formatEventTimeRange(eventBooked.event_start_time, eventBooked.event_end_time)}
                                                        </Text>
                                                        {eventBooked.metadata?.attendees && eventBooked.metadata.attendees.length > 0 && (
                                                            <Box fontSize="sm">
                                                                <Text as="span" fontWeight="semibold">Attendees: </Text>
                                                                {eventBooked.metadata.attendees.map((att, idx) => (
                                                                    <Box as="span" key={att.email} display="inline-flex" alignItems="center" mr={1}>
                                                                        {att.displayName && att.displayName !== att.email ? (
                                                                            <span>{att.displayName}, </span>
                                                                        ) : null}
                                                                        <Box as="span" px={1} borderRadius="sm" fontFamily="mono" fontSize="xs" display="inline-flex" alignItems="center">
                                                                            {att.email}
                                                                            <Button
                                                                                size="xs"
                                                                                ml={1}
                                                                                bg="gray.200"
                                                                                variant="ghost"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    navigator.clipboard.writeText(att.email);
                                                                                    toast({ status: 'success', title: 'Email copied!' });
                                                                                }}
                                                                            >
                                                                                Copy
                                                                            </Button>
                                                                        </Box>
                                                                        {idx < eventBooked.metadata.attendees.length - 1 && <span>, </span>}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                </Box>
                                            )}
                                            {/* Notes Section */}
                                            {call.extracted_call_details?.notes && (
                                                <Box mt={4} p={4} bg="blue.50" borderRadius="md">
                                                    <Heading size="sm" mb={3} display="flex" alignItems="center" gap={2}>
                                                        <FontAwesomeIcon icon={faStickyNote} />
                                                        <Text>Notes</Text>
                                                    </Heading>
                                                    <Stack spacing={2}>
                                                        {getLastTwoNonEmptyLines(call.extracted_call_details.notes).map((note, idx) => (
                                                            <Text key={`note-${call.call_sid}-${idx}`} fontSize="sm" color="blue.700">
                                                                {truncateNote(note)}
                                                            </Text>
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            )}
                                        </Box>
                                        {/* Transcript Section */}
                                        <Box flex={1}>
                                            <Flex justifyContent="flex-start" mb={4} alignItems="center" gap={3}>
                                                <Heading size="md">Transcript</Heading>
                                                {showEmailForwardedIcon && (
                                                    <Tooltip label={notifiedEmails.join(', ')} fontSize="sm" hasArrow>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <FontAwesomeIcon icon={faPaperPlane} />
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                            </Flex>
                                            {call.transcript && typeof call.transcript === "string" ? (
                                                <Box bg="gray.50" p={3} borderRadius="md" whiteSpace="pre-line" maxHeight="200px" overflowY="auto">
                                                    <Text fontSize="sm">{call.transcript}</Text>
                                                </Box>
                                            ) : (
                                                <Text color="gray.500">No transcript available.</Text>
                                            )}
                                        </Box>
                                    </Stack>
                                </CardBody>
                            </Collapse>
                        </Card>
                    );
                })}
                {calls.length === 0 && !loading && (
                    <Box textAlign="center" py={4} color="gray.500">
                        No calls found
                    </Box>
                )}
            </Stack>
            <SimplePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
            />

            {/* Notes Modal */}
            <Modal isOpen={isNotesOpen} onClose={onNotesClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Add Notes</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Textarea
                            value={notesText}
                            onChange={(e) => setNotesText(e.target.value)}
                            placeholder="Enter your notes about this call..."
                            rows={6}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onNotesClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="teal"
                            onClick={handleSaveNotes}
                            isLoading={savingNotes}
                        >
                            Save Notes
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Feedback Modal */}
            <Modal isOpen={isFeedbackOpen} onClose={onFeedbackClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Add Feedback</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Textarea
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            placeholder="Enter your feedback about this call..."
                            rows={6}
                        />
                        <Text fontSize="sm" color="gray.500" mt={2}>
                            Your feedback is valuable and helps us improve our services.
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onFeedbackClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="blue"
                            onClick={handleSaveFeedback}
                            isLoading={savingFeedback}
                        >
                            Save Feedback
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <EditEvent
                isOpen={isEditEventOpen}
                onClose={onEditEventClose}
                eventData={editEventData}
                onSave={handleSaveEvent}
            />
        </Box>
    );
};

export default DataDisplay;